import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setSectionSubtab } from 'app/slices/ahuBoardSlice';
import {
  ahuBoardApi,
  useGetSectionDmountsQuery,
  useGetSectionVerticalIntWallsQuery,
  useGetSectionHorizontalIntWallsQuery,
  useGetSectionComponentsQuery,
} from 'app/services/ahuBoardApi';
import {
  usePatchAhuCasingMutation,
  usePatchConcreteRowMutation,
  ahuBoardFormsApi,
  type ICatalogRow,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection, IAhuBoardTunnel, IAhuBoardUnit, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  getContenteditableText,
  validateNumberField,
  catalogLabel,
  formatDecimalDisplay,
} from '../../forms/formUtils';
import PaginatedSelect from '../../forms/PaginatedSelect';
import { queueSectionPatch } from '../../forms/sectionPatchQueue';
import {
  SECTION_AIR_COMMIT_KEYS,
  commitSectionAirConditions,
  hasAnyKey,
} from '../../forms/commitAirConditions';
import {
  checkSectionChangesAndValidate,
  validateTunnel,
} from '../../validation/validationController';
import { refetchHybridLugPlacements } from '../../boardActions';
import { cmsAirStreams } from '../../forms/component/formCommon';
import { deriveSeasonValue } from '../../forms/cmsDoc';
import {
  SeasonSelectRow,
  SeasonHeadingRow,
  seasonShowFlags,
  MIN_AIR_TEMP_F,
} from '../../forms/SeasonGroup';
import { BareSelect } from '../../forms/formKit';
import InternalWallsGroup from './InternalWallsGroup';
import DmountGroup from './DmountGroup';

/**
 * Phase 6e1 — per-section form (§7.4, tab.section.js + tab.section.tmpl,
 * markup faithful; the hidden duplicate tag/length lis kept as `.hide`).
 * Saves go through the §19.2 abort+merge queue; every save runs the §22.4
 * checkChangesAndValidate routing + the documented §19.2 cascades.
 */

const COMPONENT_PLURALS: Record<string, string> = {
  fan: 'fans',
  coil: 'coils',
  filter: 'filters',
  eliminator: 'eliminators',
  evap_pack: 'evap_packs',
  gas_heater: 'gas_heaters',
  silencer: 'silencers',
  energy_wheel: 'energy_wheels',
  plate_heat_exchanger: 'plate_heat_exchangers',
  uds: 'user_defined_components',
  user_defined_component: 'user_defined_components',
};

interface SectionFormProps {
  ahu: IAhuBoardUnit;
  section: IAhuBoardSection;
  tunnel: IAhuBoardTunnel | undefined;
  cms: ICmsAhuDoc | undefined;
  mfgId: number;
  active: boolean;
}

const SectionForm = ({ ahu, section, tunnel, cms, mfgId, active }: SectionFormProps) => {
  const dispatch = useAppDispatch();
  const [patchCasing] = usePatchAhuCasingMutation();
  const [patchConcrete] = usePatchConcreteRowMutation();
  // Subtab expansion lives in the slice (6e1 perf fix) — only the visible
  // form is mounted, so local state would reset on every remount.
  const openSubtab = useAppSelector(
    (s) => s.ahuBoard.sectionsTab.openSubtabBySection[section.id] ?? null
  );
  const [emptyRows, setEmptyRows] = useState<Record<number, boolean>>({});
  const [blankOffList, setBlankOffList] = useState<ICatalogRow[]>([]);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const ccid = ahu.construction_class_id ?? null;
  const isPlenum = String(section.type ?? '').toLowerCase() === 'plenum';
  const cid = `s${section.id}`;

  const { data: dmounts, refetch: refetchDmounts } = useGetSectionDmountsQuery(section.id);
  const { data: vWalls, refetch: refetchV } = useGetSectionVerticalIntWallsQuery(section.id);
  const { data: hWalls, refetch: refetchH } = useGetSectionHorizontalIntWallsQuery(section.id);
  const { data: componentRows } = useGetSectionComponentsQuery(section.id);

  const walls = [...(vWalls ?? []), ...(hWalls ?? [])];
  const refetchWalls = () => {
    void refetchV();
    void refetchH();
  };

  // Blank-off list = fn['current.blank_off_list'] (§20.9 #77 — casing
  // liner_metal_sheets page 1) ∪ the section internal-wall metal sheets, the
  // catalog behind the Internal Wall "Material" select. Both are
  // per-(mfg, construction class) selections out of the one master table
  // MasterAHUSheetMetal, and blank_off_mtl_gauge/bulkhead_wall_mtl_gauge are
  // plain FKs to it, so adding the second list only widens what is selectable —
  // a value saved from either list stays valid and still resolves.
  // De-duplicated by sheet id with the casing liners first, so the legacy
  // ordering of the existing options is untouched.
  useEffect(() => {
    let cancelled = false;
    const subs = [
      dispatch(
        ahuBoardFormsApi.endpoints.getCmsPaginated.initiate({
          mfgId,
          path: 'ahu/casing/liner_metal_sheets',
          page: 1,
          constructionClassId: ccid,
        })
      ),
    ];
    // The internal-wall catalog is construction-class scoped and its endpoint
    // 400s without construction_class_id (viewset.py:3045), so an AHU with no
    // class keeps the casing-liner list alone rather than losing both.
    if (ccid != null) {
      subs.push(
        dispatch(
          ahuBoardFormsApi.endpoints.getCmsPaginated.initiate({
            mfgId,
            path: 'ahu/section_internal_wall/metal_sheets',
            page: 1,
            constructionClassId: ccid,
          })
        )
      );
    }
    // allSettled, not all: one list failing must not empty the other.
    void Promise.allSettled(subs.map((sub) => sub.unwrap())).then((pages) => {
      if (cancelled) return;
      const seen = new Set<number>();
      const merged: ICatalogRow[] = [];
      pages.forEach((page) => {
        if (page.status !== 'fulfilled') return;
        page.value.results.forEach((row) => {
          if (seen.has(row.id)) return;
          seen.add(row.id);
          merged.push(row);
        });
      });
      setBlankOffList(merged);
    });
    return () => {
      cancelled = true;
      subs.forEach((sub) => sub.unsubscribe());
    };
  }, [mfgId, ccid, dispatch]);

  // §13 — blur of any section field returns focus to the canvas (changeFocus).
  const focusCanvas = () => {
    document.getElementById('canvas_section')?.focus();
  };

  // First component (blank-off target + §22.4 routing).
  const firstComponent = (componentRows ?? [])[0];
  const componentPlural = firstComponent
    ? COMPONENT_PLURALS[String(firstComponent.type ?? '')]
    : undefined;
  const isFan = String(firstComponent?.type ?? '') === 'fan';
  const blankOffField = isFan ? 'bulkhead_wall_mtl_gauge_id' : 'blank_off_mtl_gauge_id';
  const [blankOffValue, setBlankOffValue] = useState<number | null>(null);

  // Blank-off current value lives on the concrete component (§7.4).
  useEffect(() => {
    if (!firstComponent || !componentPlural) return undefined;
    let cancelled = false;
    const sub = dispatch(
      ahuBoardApi.endpoints.getAccessoryDetail.initiate({
        plural: componentPlural,
        id: firstComponent.id,
      })
    );
    sub
      .unwrap()
      .then((detail) => {
        if (!cancelled) {
          const v = detail[blankOffField];
          setBlankOffValue(typeof v === 'number' ? v : null);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [firstComponent?.id, componentPlural, blankOffField, dispatch]);

  const isFirstOrLast = (() => {
    // first/last section in tunnel (validation routing §22.4) — derived from
    // the cached tunnel section list ordering.
    return true; // conservative: tunnel-level revalidation (cheapest correct upper bound)
  })();

  const saveSection = (changes: Record<string, unknown>) => {
    // Dev-sync J4 — the synchronize gate (section.js:200-284): a PLENUM edit
    // of the _summer entering pair while sync is on routes through POST
    // sections/<id>/commit_air_conditions (server-side cascade + needs_rerate
    // flagging) instead of the plain PATCH queue, with autoReload:false.
    // ★ Only the _summer aliases gate — legacy checks exactly those two keys
    // (a winter-only edit would fall through). The evap pack's equivalent
    // lives in useComponentForm.commitSectionField (its section inputs render
    // in EvapPackForm, not here). Sync flag: the ahu prop mirrors the
    // getAhuUnit cache, which the chart's toggle keeps fresh.
    const boardSyncOn = !!(ahu as Record<string, unknown>).synchronize_air_conditions;
    if (isPlenum && boardSyncOn && hasAnyKey(changes, SECTION_AIR_COMMIT_KEYS)) {
      void commitSectionAirConditions({
        dispatch,
        url: `/api/v1.0/ahu/sections/${section.id}/commit_air_conditions`,
        sectionId: section.id,
        tunnelId: section.tunnel_id,
        changes,
        onCommit: () => {
          // legacy onCommit runs checkChangesAndValidate(c) (section.js:230).
          void dispatch(
            checkSectionChangesAndValidate({
              sectionId: section.id,
              tunnelId: section.tunnel_id,
              sectionLabel: String(section.label ?? section.name ?? ''),
              changes,
              isFirstOrLast,
              firstComponentId: firstComponent?.id ?? null,
            })
          );
        },
      });
      return;
    }
    queueSectionPatch({
      dispatch,
      sectionId: section.id,
      tunnelId: section.tunnel_id,
      changes,
      onSuccess: (saved, applied) => {
        void dispatch(
          checkSectionChangesAndValidate({
            sectionId: section.id,
            tunnelId: section.tunnel_id,
            sectionLabel: String(saved.label ?? saved.name ?? ''),
            changes: applied,
            isFirstOrLast,
            firstComponentId: firstComponent?.id ?? null,
          })
        );
        // §19.2 section cascades. (J4: the _summer aliases joined the gate —
        // the plenum inputs now write eadb_temp_summer/eawb_temp_summer, and
        // section.js:290-298 fires the same event on the plain-PATCH path.)
        if (
          'eadb_temp' in applied ||
          'eawb_temp' in applied ||
          'eadb_temp_summer' in applied ||
          'eawb_temp_summer' in applied
        ) {
          ahuBoardBus.emit(`changed:section:${section.id}:eadb_temp:eawb_temp`);
        }
        if (
          'casing_insulation_id' in applied ||
          // 5e-sync — dev 290f69d177 (section/model/section.js:200-206): the
          // inner-liner fields ALSO fire this event so the coil selector's
          // Available-AW pair refreshes on any cabinet-thickness change.
          'casing_innerliner_id' in applied ||
          'floor_innerliner_id' in applied
        ) {
          // typo is load-bearing (§19.2).
          ahuBoardBus.emit(`changed:secrion_${section.id}:casing_insulation_id`);
        }
        if ('length' in applied || 'casing_insulation_id' in applied) {
          ahuBoardBus.emit('change:tunnel:dimensions', {
            tunnel_id: section.tunnel_id,
            render: true,
            only_for: section.id,
          });
          // A section LENGTH change is the same class of gesture as a tunnel
          // W/H edit and takes the same hybrid fork (refetchHybridLugPlacements).
          // `length` is the whole of `_SECTION_GROUP_RELEVANT_FIELDS`
          // (base_group_orchestration.py:267), so `_section_post_save` runs
          // `_recompute_for_ahu_id` → `recompute_base_groups` →
          // `sync_hybrid_lugs` before the PATCH answers: the tunnel's x-span
          // moves, every base group whose `_content_key` it changes is deleted
          // and recreated (so `is_new`, the one case that re-lays even with
          // `ahu.lug_defaults` off), and the runs are re-laid AHU-wide.
          // `tunnelLugs:recalcPlacement` then hides a placement the server has
          // already corrected and nothing re-reads it — the reported "increase
          // the first section's length and that section's lugs disappear"
          // (AHU 6727: after the edit its left/right runs still hold positions
          // at x=1 and x=44 across the 0-48 span, drawn by neither tunnel).
          // Not forked for `casing_insulation_id` on its own: it is not a
          // group-relevant field, so nothing has moved server-side and
          // legacy's arm-ReCalculate contract is still the right one there.
          if (ahu.is_hybrid && 'length' in applied) {
            void dispatch(refetchHybridLugPlacements(ahu.id, { hideWhilePending: true }));
          } else {
            ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
              tunnel_id: section.tunnel_id,
              hide_from_canvas: true,
            });
          }
          if (isFan) ahuBoardBus.emit(`refetch_fan_${section.id}_isp`);
        }
        if ('casing_innerliner_id' in applied || 'floor_innerliner_id' in applied || 'floor_finish_id' in applied) {
          ahuBoardBus.emit(`change:casing:section_${section.id}`);
          ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
            tunnel_id: section.tunnel_id,
            hide_from_canvas: true,
          });
        }
      },
    });
  };

  // Tunnel-level casing change routed from the section form (§7.4 item 1).
  const changeCasing = (field: 'outerliner_id' | 'finish_id', id: number | null) => {
    if (tunnel?.casing_id == null) return;
    patchCasing({ id: Number(tunnel.casing_id), data: { [field]: id } })
      .unwrap()
      .then(() => {
        // QA5 #7 — the select reads section.outerliner_id/finish_id from the
        // sections cache, which nothing refreshed: the pick visibly snapped
        // back until a page reload. Legacy updates every section form at once
        // via the tunnel.casing listeners (tab.section.js:133-147) — write
        // the tunnel's section rows optimistically to match.
        dispatch(
          ahuBoardApi.util.updateQueryData(
            'getTunnelSectionList',
            section.tunnel_id,
            (rows) => rows.map((row) => ({ ...row, [field]: id }))
          )
        );
        if (field === 'outerliner_id') {
          ahuBoardBus.emit('change:tunnel:casing', { tunnel_id: section.tunnel_id });
          void dispatch(validateTunnel(section.tunnel_id));
        }
        // Echo back into every section's selects → refetch tunnels cache.
        dispatch(
          ahuBoardApi.util.invalidateTags([{ type: 'AhuTunnels', id: ahu.id }])
        );
      })
      .catch(() => undefined);
  };

  const changeBlankOff = (id: number | null) => {
    if (!firstComponent || !componentPlural) return;
    // Blank-off persists via the component model (filter+rated special case
    // PATCHes the same endpoint directly — identical wire shape here).
    patchConcrete({
      plural: componentPlural,
      id: firstComponent.id,
      data: { [blankOffField]: id },
    })
      .unwrap()
      .then(() => setBlankOffValue(id))
      .catch(() => undefined);
  };

  // `change:section:tag:<id>` (tag strip sender §7.1) → model.set({tag}).
  useEffect(() => {
    const off = ahuBoardBus.on(`change:section:tag:${section.id}`, (...args: unknown[]) => {
      const tag = String(args[0] ?? '');
      saveSection({ tag });
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.id, section.tunnel_id, firstComponent?.id]);

  const toggleSubtab = (key: string) =>
    dispatch(
      setSectionSubtab({
        sectionId: section.id,
        subtab: openSubtab === key ? null : key,
      })
    );

  const hiddenIfEmpty = (idx: number) => (emptyRows[idx] ? { display: 'none' } : undefined);
  const markEmpty = (idx: number) => (empty: boolean) =>
    setEmptyRows((prev) => (prev[idx] === empty ? prev : { ...prev, [idx]: empty }));

  const tempInput = (
    name: string,
    value: unknown,
    max: number,
    opts?: { placeholder?: unknown; tabIndex?: number }
  ) => (
    <input
      type="text"
      data-type="number"
      // -40 °F, matching the air-temperature floor the section's own
      // validators now carry (ahu migration 0152) and the left edge of
      // the psychrometric chart's cold scale.
      data-min={String(MIN_AIR_TEMP_F)}
      data-step="0.01"
      data-max={String(max)}
      name={name}
      className="section_field medium-input"
      defaultValue={formatDecimalDisplay(value)}
      // J6 — the LADB/LAWB placeholder-shows-entering pattern
      // (tab.section.tmpl: placeholder="{{section.eadb_temp_summer}}" +
      // formatPlaceholder, tab.section.js:706-718 — decimal w/ commas).
      placeholder={
        opts?.placeholder == null ||
        opts.placeholder === '' ||
        Number.isNaN(Number(opts.placeholder))
          ? undefined
          : formatDecimalDisplay(opts.placeholder)
      }
      tabIndex={opts?.tabIndex}
      onBlur={(e) => {
        const res = validateNumberField(e.currentTarget.value, {
          // Same floor as data-min above — with min:0 here a -20 °F winter
          // design day was rejected on blur and never reached saveSection,
          // so the box just turned red.
          min: MIN_AIR_TEMP_F,
          max,
          mode: 'reject',
          step: 0.01,
        });
        if (res.ok) {
          // Legacy only ever ADDS `.invalid` (tab.section.js:733) — it clears
          // it by re-rendering the form, which rebuilds the input. This input
          // survives the re-render, so an accepted value has to drop the class
          // itself or the box stays red after the user has corrected it.
          e.currentTarget.classList.remove('invalid');
          e.currentTarget.value = formatDecimalDisplay(res.value);
          saveSection({ [name]: res.value });
          focusCanvas();
        } else {
          e.currentTarget.classList.add('invalid');
        }
      }}
    />
  );

  // J6 — section-level select commit (legacy changeSelectField,
  // tab.section.js:588-600: `value = Number(value) || null` then model.set →
  // the abort+merge PATCH). The Summer/Winter groups' visibility re-derives
  // from season_id inline (survey §C5 item 2 — season_value never recomputed
  // client-side).
  const saveSectionSelect = (name: string, raw: string) => {
    saveSection({ [name]: Number(raw) || null });
  };

  return (
    <div
      id={`form_section_${section.id}`}
      className={`section${active ? ' active' : ''}`}
      ref={rootRef}
    >
      <label className="section-tag hide">
        <span className="label">Section</span>
        <input
          type="text"
          name="tag"
          maxLength={36}
          className="section_field"
          defaultValue={String(section.tag ?? '')}
          readOnly
        />
      </label>
      <div className="section-cabinet-list menu-item ahu-left-container">
        <div className="inputs-group">
          <ul className="sublist">
            <li className="hide">
              <div>
                <input
                  type="text"
                  name="tag"
                  maxLength={36}
                  className="section_field"
                  defaultValue={String(section.tag ?? '')}
                  readOnly
                />
                <span className="label">{String(section.name ?? '')} section</span>
              </div>
            </li>
            <li data-select-index="0" style={hiddenIfEmpty(0)}>
              <div className="single-line-with-custom-select">
                <div id="outer-skin-select" className="select-container">
                  <PaginatedSelect
                    mfgId={mfgId}
                    path="ahu/casing/metal_sheets"
                    constructionClassId={ccid}
                    value={(section.outerliner_id as number | null | undefined) ?? null}
                    onChange={(id) => changeCasing('outerliner_id', id)}
                    selectName="outerliner_id"
                    selectClass="section_field"
                    hasNoneOption={false}
                    onEmptyChange={markEmpty(0)}
                  />
                </div>
                <span className="label">Tunnel Casing Outer Skin</span>
              </div>
            </li>
            <li data-select-index="1" style={hiddenIfEmpty(1)}>
              <div className="single-line-with-custom-select">
                <div id="skin-finish-select" className="select-container">
                  {/* Legacy tab.section.js selects[1] api is floor/finishes
                      (quirk preserved — NOT casing/finishes). */}
                  <PaginatedSelect
                    mfgId={mfgId}
                    path="ahu/floor/finishes"
                    constructionClassId={ccid}
                    value={(section.finish_id as number | null | undefined) ?? null}
                    onChange={(id) => changeCasing('finish_id', id)}
                    selectName="finish_id"
                    selectClass="section_field"
                    onEmptyChange={markEmpty(1)}
                  />
                </div>
                <span className="label">Tunnel Casing Skin Finish</span>
              </div>
            </li>
            <li data-select-index="2" style={hiddenIfEmpty(2)}>
              <div className="single-line-with-custom-select">
                <div id="casing-insulation-select" className="select-container">
                  <PaginatedSelect
                    mfgId={mfgId}
                    path="ahu/casing/insulations"
                    constructionClassId={ccid}
                    value={section.casing_insulation_id ?? null}
                    onChange={(id) => saveSection({ casing_insulation_id: id })}
                    selectName="casing_insulation_id"
                    selectClass="section_field"
                    onEmptyChange={markEmpty(2)}
                  />
                </div>
                <span className="label">Casing Insulation</span>
              </div>
            </li>
            <li data-select-index="3" style={hiddenIfEmpty(3)}>
              <div className="single-line-with-custom-select">
                <div id="casing-innerliner-select" className="select-container">
                  <PaginatedSelect
                    mfgId={mfgId}
                    path="ahu/casing/liner_metal_sheets"
                    constructionClassId={ccid}
                    value={section.casing_innerliner_id ?? null}
                    onChange={(id) => saveSection({ casing_innerliner_id: id })}
                    selectName="casing_innerliner_id"
                    selectClass="section_field"
                    onEmptyChange={markEmpty(3)}
                  />
                </div>
                <span className="label">Casing Inner Liner</span>
              </div>
            </li>
            <li data-select-index="4" style={hiddenIfEmpty(4)}>
              <div className="single-line-with-custom-select">
                <div id="floor-innerliner-select" className="select-container">
                  <PaginatedSelect
                    mfgId={mfgId}
                    path="ahu/floor/metal_sheets"
                    constructionClassId={ccid}
                    value={section.floor_innerliner_id ?? null}
                    onChange={(id) => saveSection({ floor_innerliner_id: id })}
                    selectName="floor_innerliner_id"
                    selectClass="section_field"
                    onEmptyChange={markEmpty(4)}
                  />
                </div>
                <span className="label">Floor Deck</span>
              </div>
            </li>
            <li data-select-index="5" style={hiddenIfEmpty(5)}>
              <div className="single-line-with-custom-select">
                <div id="floor-finish-select" className="select-container">
                  <PaginatedSelect
                    mfgId={mfgId}
                    path="ahu/floor/finishes"
                    constructionClassId={ccid}
                    value={section.floor_finish_id ?? null}
                    onChange={(id) => saveSection({ floor_finish_id: id })}
                    selectName="floor_finish_id"
                    selectClass="section_field"
                    onEmptyChange={markEmpty(5)}
                  />
                </div>
                <span className="label">Floor Deck Finish</span>
              </div>
            </li>
            <li
              className={`floor_under_insulation-li${
                section.floor_under_insulation ? '' : ' hide'
              }`}
            >
              <div>
                <input
                  type="text"
                  className="section_field"
                  value={String(section.floor_under_insulation ?? '')}
                  title={String(section.floor_under_insulation ?? '')}
                  name="floor_under_insulation"
                  readOnly
                  onChange={() => undefined}
                />
                <span className="label">Under-Insulation</span>
              </div>
            </li>
            <li
              className={`floor_under_liner_mtl_gauge-li${
                section.floor_under_liner_mtl_gauge ? '' : ' hide'
              }`}
            >
              <div>
                <input
                  type="text"
                  className="section_field"
                  value={String(section.floor_under_liner_mtl_gauge ?? '')}
                  title={String(section.floor_under_liner_mtl_gauge ?? '')}
                  name="floor_under_liner_mtl_gauge"
                  readOnly
                  onChange={() => undefined}
                />
                <span className="label">Under-Liner</span>
              </div>
            </li>
            <li className="blank_off_container">
              {firstComponent && blankOffList.length > 0 && (
                <div className="single-line">
                  <span className="selection">
                    {blankOffList
                      .filter((b) => b.id === blankOffValue)
                      .map((b) => (
                        <span key={b.id}>{catalogLabel(b)}</span>
                      ))}
                  </span>
                  <select
                    name={blankOffField}
                    className="model_field"
                    required
                    value={blankOffValue ?? 'None'}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      changeBlankOff(v === 'None' ? null : Number(v));
                    }}
                  >
                    <option value="None">- -</option>
                    {blankOffList.map((b) => (
                      <option key={b.id} value={b.id}>
                        {catalogLabel(b)}
                      </option>
                    ))}
                  </select>
                  <span className="label">Blank-Off Wall</span>
                </div>
              )}
            </li>
          </ul>

          <InternalWallsGroup
            section={section}
            walls={walls}
            refetchWalls={refetchWalls}
            open={openSubtab === 'int_wall'}
            onToggle={() => toggleSubtab('int_wall')}
            cid={cid}
            mfgId={mfgId}
            constructionClassId={ccid}
          />

          <DmountGroup
            section={section}
            tunnel={tunnel}
            cms={cms}
            dmounts={dmounts ?? []}
            refetchDmounts={() => void refetchDmounts()}
            open={openSubtab === 'd_mount'}
            onToggle={() => toggleSubtab('d_mount')}
            cid={cid}
          />

          {isPlenum && (
            <ul className="inputs-group performance_group" id="inputs-group_performance_group">
              <li className="sublist-group">
                <input
                  id={`plenum_perf_${cid}`}
                  data-subtab="performance"
                  type="checkbox"
                  name="ahu_left_bar"
                  checked={openSubtab === 'performance'}
                  onChange={() => toggleSubtab('performance')}
                  className="sublist-ctrl-checkbox section_subtab"
                />
                <label htmlFor={`plenum_perf_${cid}`}>
                  Performance
                  <i className="icon-add" />
                </label>
                {/* Dev-sync J6 — the season split (tab.section.tmpl
                    +157..234): Air Stream + Season selects, then the
                    season_id-gated Summer/Winter EADB/EAWB + LADB/LAWB
                    groups (the old unsuffixed LADB/LAWB row is GONE). The
                    plenum tmpl gates only on `{{#if section.season_id}}` and
                    lets the [data-season] CSS hide the off-season half —
                    JSX gates both axes (survey §C5 item 4), keeping the
                    data-season attr + season-group-* classes for the merged
                    CSS. J4's eadb/eawb_temp_summer rename (pure @property
                    aliases) moved inside the gate, matching legacy. */}
                <ul
                  className="sublist plenum-perf"
                  data-season={deriveSeasonValue(cms, section.season_id)}
                >
                  <li>
                    <div>
                      <span className="unit" />
                      {/* sync K (tab.section.tmpl delta @163): the None option
                          loses `hide` — "- -" is selectable, unsetting the air
                          stream (saveSectionSelect: Number('None')→NaN→null,
                          legacy's `Number(value) || null`). season_id below
                          keeps its hidden None. */}
                      <BareSelect
                        name="air_stream_id"
                        fieldClass="section_field"
                        value={section.air_stream_id}
                        options={cmsAirStreams(cms)}
                        noneMode="visible"
                        onChange={(raw) => saveSectionSelect('air_stream_id', raw)}
                      />
                      <span className="label">Air Stream</span>
                    </div>
                  </li>
                  <SeasonSelectRow
                    cms={cms}
                    component="Plenum"
                    value={section.season_id}
                    onChange={(raw) => saveSectionSelect('season_id', raw)}
                  />
                  {(() => {
                    const { showSummer, showWinter } = seasonShowFlags(section.season_id);
                    const sec = section as Record<string, unknown>;
                    return (
                      <>
                        {showSummer && (
                          <>
                            <SeasonHeadingRow season="summer" />
                            <li className="season-group-summer">
                              <div className="efficiency-mech">
                                <span className="unit temp"> F </span>
                                {tempInput(
                                  'eadb_temp_summer',
                                  sec.eadb_temp_summer ?? section.eadb_temp,
                                  400000
                                )}
                                {tempInput(
                                  'eawb_temp_summer',
                                  sec.eawb_temp_summer ?? section.eawb_temp,
                                  400000
                                )}
                                <span className="label">
                                  <span>EADB</span> / <span>EAWB</span>
                                </span>
                              </div>
                            </li>
                            <li className="season-group-summer">
                              <div>
                                <span className="unit temp"> F </span>
                                {tempInput('ladb_temp_summer', sec.ladb_temp_summer, 240, {
                                  placeholder: sec.eadb_temp_summer ?? section.eadb_temp,
                                  tabIndex: -1,
                                })}
                                {tempInput('lawb_temp_summer', sec.lawb_temp_summer, 240, {
                                  placeholder: sec.eawb_temp_summer ?? section.eawb_temp,
                                  tabIndex: -1,
                                })}
                                <span className="label">LADB / LAWB</span>
                              </div>
                            </li>
                          </>
                        )}
                        {showWinter && (
                          <>
                            <SeasonHeadingRow season="winter" />
                            <li className="season-group-winter">
                              <div className="efficiency-mech">
                                <span className="unit temp"> F </span>
                                {tempInput('eadb_temp_winter', sec.eadb_temp_winter, 400000)}
                                {tempInput('eawb_temp_winter', sec.eawb_temp_winter, 400000)}
                                <span className="label">
                                  <span>EADB</span> / <span>EAWB</span>
                                </span>
                              </div>
                            </li>
                            <li className="season-group-winter">
                              <div>
                                <span className="unit temp"> F </span>
                                {tempInput('ladb_temp_winter', sec.ladb_temp_winter, 240, {
                                  placeholder: sec.eadb_temp_winter,
                                  tabIndex: -1,
                                })}
                                {tempInput('lawb_temp_winter', sec.lawb_temp_winter, 240, {
                                  placeholder: sec.eawb_temp_winter,
                                  tabIndex: -1,
                                })}
                                <span className="label">LADB / LAWB</span>
                              </div>
                            </li>
                          </>
                        )}
                      </>
                    );
                  })()}
                </ul>
              </li>
            </ul>
          )}

          <ul className="inputs-group remarks">
            <li className="sublist-group">
              <input
                id={`remarks_${cid}`}
                data-subtab="remarks"
                type="checkbox"
                name="ahu_left_bar"
                checked={openSubtab === 'remarks'}
                onChange={() => toggleSubtab('remarks')}
                className="sublist-ctrl-checkbox section_subtab"
              />
              <label htmlFor={`remarks_${cid}`}>
                Remarks
                <i className="icon-add" />
                <span className="short_remarks">{String(section.remarks ?? '')}</span>
              </label>
              <ul className="sublist">
                <li>
                  <div
                    {...{ maxlength: 300, placeholder: '300 characters maximum', name: 'remarks' }}
                    className="textarea remarks section_field remarks-area"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => {
                      saveSection({
                        remarks: getContenteditableText(e.currentTarget, 300),
                      });
                      focusCanvas();
                    }}
                  >
                    {String(section.remarks ?? '')}
                  </div>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SectionForm;
